import React from 'react';
import {
  ShoppingBag, RefreshCw, AlertCircle, Calendar, Mail, Phone,
  User, Building2, FileText, Eye, Trash2, Loader2, X, Award,
  Package, DollarSign, CreditCard,
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  getCostForItem, getStatusInfo, formatDate, formatCurrency,
  formatCPF, formatCNPJ, formatPhone, getLicenseStatus, STATUS_LIST,
} from './adminHelpers';
import { StatusBadge, LicenseBadge } from './StatusBadge';

const AdminPedidos = ({
  orders, loading, error, filter, setFilter,
  selectedOrder, setSelectedOrder,
  deleteConfirm, setDeleteConfirm,
  actionLoading, costConfig,
  updateStatus, deleteOrder, exportOrderPDF,
}) => {
  const approved  = orders.filter(o => o.payment_status === 'approved');
  const pending   = orders.filter(o => !o.payment_status || o.payment_status === 'pending');
  const licensed  = orders.filter(o => o.payment_status === 'license_generated');
  const cancelled = orders.filter(o => ['rejected', 'cancelled', 'refunded'].includes(o.payment_status));
  const revenue   = [...approved, ...licensed].reduce((s, o) => s + (o.amount_paid || o.total || 0), 0);
  const totalCost = [...approved, ...licensed].reduce((s, o) =>
    s + (o.items || []).reduce((s2, i) => s2 + getCostForItem(i.name, costConfig) * (i.quantity || 1), 0), 0);

  const filteredOrders = orders.filter(o => {
    if (filter === 'all') return true;
    if (filter === 'approved') return o.payment_status === 'approved';
    if (filter === 'pending') return !o.payment_status || o.payment_status === 'pending';
    if (filter === 'license_generated') return o.payment_status === 'license_generated';
    if (filter === 'cancelled') return ['rejected', 'cancelled', 'refunded'].includes(o.payment_status);
    return true;
  });

  return (
    <>
      {/* Quick Stats */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2 p-3 bg-slate-800/50 border-b border-slate-700 shrink-0">
        {[
          { label: 'Total',         value: orders.length,                          color: 'text-white'       },
          { label: 'Aprovados',     value: approved.length,                        color: 'text-green-400'   },
          { label: 'Licença',       value: licensed.length,                        color: 'text-blue-400'    },
          { label: 'Pendentes',     value: pending.length,                         color: 'text-yellow-400'  },
          { label: 'Faturamento',   value: formatCurrency(revenue),                color: 'text-cyan-400'    },
          { label: 'Lucro',         value: formatCurrency(revenue - totalCost),    color: 'text-emerald-400' },
        ].map((s, i) => (
          <div key={i} className="bg-slate-900/50 rounded-lg p-2 text-center">
            <div className="text-xs text-slate-500">{s.label}</div>
            <div className={`text-sm font-bold ${s.color}`}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="px-4 py-2 border-b border-slate-700 flex gap-2 flex-wrap shrink-0">
        {[
          { id: 'all',               label: 'Todos',         count: orders.length    },
          { id: 'approved',          label: 'Aprovados',     count: approved.length  },
          { id: 'license_generated', label: 'Licença Gerada',count: licensed.length  },
          { id: 'pending',           label: 'Pendentes',     count: pending.length   },
          { id: 'cancelled',         label: 'Cancelados',    count: cancelled.length },
        ].map(f => (
          <button key={f.id} onClick={() => setFilter(f.id)}
            className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${filter === f.id ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/50' : 'bg-slate-800 text-slate-400 border border-slate-700 hover:border-slate-600'}`}>
            {f.label} ({f.count})
          </button>
        ))}
      </div>

      {/* Orders List */}
      <div className="flex-1 overflow-y-auto p-3">
        {loading ? (
          <div className="flex items-center justify-center py-12"><RefreshCw className="w-8 h-8 text-cyan-400 animate-spin" /></div>
        ) : error ? (
          <div className="text-center py-12"><AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" /><p className="text-red-400">{error}</p></div>
        ) : filteredOrders.length === 0 ? (
          <div className="text-center py-12"><ShoppingBag className="w-12 h-12 text-slate-600 mx-auto mb-3" /><p className="text-slate-400">Nenhum pedido encontrado</p></div>
        ) : (
          <div className="space-y-2">
            {filteredOrders.map((order) => {
              const c         = order.customer || {};
              const orderCost = (order.items || []).reduce((s, i) => s + getCostForItem(i.name, costConfig) * (i.quantity || 1), 0);
              const orderSale = order.amount_paid || order.total || 0;
              return (
                <div key={order.id} className="bg-slate-800/50 border border-slate-700 rounded-xl p-3 hover:border-cyan-500/30 transition-colors">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-white font-semibold text-sm">#{order.external_reference || order.id?.slice(0, 8)}</span>
                        <StatusBadge status={order.payment_status} />
                        <LicenseBadge order={order} />
                        <span className="text-xs text-slate-500"><Calendar className="w-3 h-3 inline mr-1" />{formatDate(order.created_at)}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-300 mb-1">
                        <span className="flex items-center gap-1">
                          {c.person_type === 'cnpj' ? <Building2 className="w-3 h-3 text-cyan-400" /> : <User className="w-3 h-3 text-cyan-400" />}
                          <strong>{c.nome_completo || c.razao_social || order.customer_name || '-'}</strong>
                        </span>
                        <span className="text-slate-500">{c.cpf ? formatCPF(c.cpf) : c.cnpj ? formatCNPJ(c.cnpj) : order.customer_identifier || ''}</span>
                      </div>
                      <div className="flex flex-wrap gap-3 text-xs text-slate-500">
                        {(c.email || order.customer_email) && <span><Mail className="w-3 h-3 inline mr-1" />{c.email || order.customer_email}</span>}
                        {(c.celular || c.whatsapp || order.customer_whatsapp) && <span><Phone className="w-3 h-3 inline mr-1" />{formatPhone(c.celular || c.whatsapp || order.customer_whatsapp)}</span>}
                        <span>{(order.items || []).map(i => i.name).join(', ')}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-right mr-2">
                        <div className="text-sm font-bold text-white">{formatCurrency(orderSale)}</div>
                        <div className="text-xs text-green-400">Lucro: {formatCurrency(orderSale - orderCost)}</div>
                      </div>
                      <select value={order.payment_status || 'pending'}
                        onChange={e => updateStatus(order.id, e.target.value)}
                        disabled={actionLoading === order.id}
                        className="bg-slate-700 border border-slate-600 text-slate-200 text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-cyan-500 cursor-pointer">
                        <option value="pending">Pendente</option>
                        <option value="approved">Aprovado</option>
                        <option value="license_generated">Licença Gerada</option>
                        <option value="cancelled">Cancelado</option>
                        <option value="refunded">Reembolsado</option>
                      </select>
                      <Button onClick={() => setSelectedOrder(order)} variant="outline" size="sm" className="border-slate-600 text-slate-300 hover:bg-slate-700 px-2"><Eye className="w-4 h-4" /></Button>
                      <Button onClick={() => exportOrderPDF(order)} variant="outline" size="sm" className="border-blue-600/50 text-blue-400 hover:bg-blue-900/30 px-2"><FileText className="w-4 h-4" /></Button>
                      <Button onClick={() => setDeleteConfirm(order.id)} variant="outline" size="sm" className="border-red-600/50 text-red-400 hover:bg-red-900/30 px-2"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="absolute inset-0 bg-black/80 flex items-center justify-center z-20">
          <div className="bg-slate-900 border border-red-500/50 rounded-xl p-6 max-w-sm">
            <h3 className="text-white font-bold text-lg mb-2 flex items-center gap-2"><Trash2 className="w-5 h-5 text-red-400" />Excluir Pedido?</h3>
            <p className="text-slate-400 text-sm mb-4">Esta ação não pode ser desfeita.</p>
            <div className="flex gap-3">
              <Button onClick={() => setDeleteConfirm(null)} variant="outline" className="flex-1 border-slate-600 text-slate-300">Cancelar</Button>
              <Button onClick={() => deleteOrder(deleteConfirm)} className="flex-1 bg-red-600 hover:bg-red-700 text-white" disabled={!!actionLoading}>
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Excluir'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="absolute inset-0 bg-black/80 flex items-center justify-center p-4 z-10 overflow-y-auto">
          <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-2xl w-full p-6 my-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Detalhes do Pedido</h3>
              <div className="flex gap-2">
                <Button onClick={() => exportOrderPDF(selectedOrder)} variant="outline" size="sm" className="border-blue-600/50 text-blue-400"><FileText className="w-4 h-4 mr-1" />PDF</Button>
                <button onClick={() => setSelectedOrder(null)} className="p-1 text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-800/50 p-3 rounded-lg"><span className="text-xs text-slate-500">Referência</span><p className="text-white font-mono text-sm">{selectedOrder.external_reference || '-'}</p></div>
                <div className="bg-slate-800/50 p-3 rounded-lg"><span className="text-xs text-slate-500">Status</span><div className="mt-1"><StatusBadge status={selectedOrder.payment_status} /></div></div>
                <div className="bg-slate-800/50 p-3 rounded-lg"><span className="text-xs text-slate-500">Data</span><p className="text-white text-sm">{formatDate(selectedOrder.created_at)}</p></div>
                <div className="bg-slate-800/50 p-3 rounded-lg"><span className="text-xs text-slate-500">Pagamento</span><p className="text-white text-sm">{selectedOrder.payment_method || '-'} {selectedOrder.payment_id ? `(${selectedOrder.payment_id})` : ''}</p></div>
              </div>
              {/* Status Update */}
              <div className="bg-slate-800/50 p-3 rounded-lg">
                <span className="text-xs text-slate-500 block mb-2">Alterar Status</span>
                <div className="flex gap-2 flex-wrap">
                  {STATUS_LIST.map(s => (
                    <button key={s.id} onClick={() => updateStatus(selectedOrder.id, s.id)}
                      disabled={actionLoading === selectedOrder.id || selectedOrder.payment_status === s.id}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors flex items-center gap-1 ${selectedOrder.payment_status === s.id ? `bg-${s.color}-500/30 text-${s.color}-400 border-${s.color}-500/50` : 'bg-slate-700 text-slate-300 border-slate-600 hover:border-slate-500'} disabled:opacity-50`}>
                      <s.icon className="w-3 h-3" />{s.label}
                    </button>
                  ))}
                </div>
              </div>
              {/* License Info */}
              {selectedOrder.license_expires_at && (
                <div className="bg-gradient-to-r from-blue-900/30 to-indigo-900/30 border border-blue-500/30 rounded-lg p-3">
                  <h4 className="text-white font-semibold mb-2 flex items-center gap-2"><Award className="w-4 h-4 text-blue-400" />Licença</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <div><span className="text-xs text-slate-500">Gerada em</span><p className="text-white text-sm">{formatDate(selectedOrder.license_generated_at)}</p></div>
                    <div><span className="text-xs text-slate-500">Expira em</span><p className="text-white text-sm">{new Date(selectedOrder.license_expires_at).toLocaleDateString('pt-BR')}</p></div>
                    <div><span className="text-xs text-slate-500">Status</span><div className="mt-1"><LicenseBadge order={selectedOrder} /></div></div>
                  </div>
                </div>
              )}
              {/* Cliente */}
              <div className="border-t border-slate-700 pt-3">
                <h4 className="text-white font-semibold mb-3 flex items-center gap-2"><User className="w-4 h-4 text-cyan-400" />Dados do Cliente</h4>
                {(() => {
                  const c    = selectedOrder.customer || {};
                  const addr = c.endereco || {};
                  return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {c.person_type === 'cpf' ? (<>
                        <div className="bg-slate-800/50 p-3 rounded-lg"><span className="text-xs text-slate-500">CPF</span><p className="text-white text-sm font-mono">{formatCPF(c.cpf)}</p></div>
                        <div className="bg-slate-800/50 p-3 rounded-lg"><span className="text-xs text-slate-500">Nome</span><p className="text-white text-sm">{c.nome_completo || '-'}</p></div>
                      </>) : c.person_type === 'cnpj' ? (<>
                        <div className="bg-slate-800/50 p-3 rounded-lg"><span className="text-xs text-slate-500">CNPJ</span><p className="text-white text-sm font-mono">{formatCNPJ(c.cnpj)}</p></div>
                        <div className="bg-slate-800/50 p-3 rounded-lg"><span className="text-xs text-slate-500">Razão Social</span><p className="text-white text-sm">{c.razao_social || '-'}</p></div>
                      </>) : (
                        <div className="bg-slate-800/50 p-3 rounded-lg"><span className="text-xs text-slate-500">Cliente</span><p className="text-white text-sm">{selectedOrder.customer_name || '-'}</p></div>
                      )}
                      <div className="bg-slate-800/50 p-3 rounded-lg"><span className="text-xs text-slate-500">Email</span><p className="text-white text-sm">{c.email || selectedOrder.customer_email || '-'}</p></div>
                      <div className="bg-slate-800/50 p-3 rounded-lg"><span className="text-xs text-slate-500">Celular</span><p className="text-white text-sm">{formatPhone(c.celular || '')}</p></div>
                      <div className="bg-slate-800/50 p-3 rounded-lg"><span className="text-xs text-slate-500">WhatsApp</span><p className="text-white text-sm">{formatPhone(c.whatsapp || selectedOrder.customer_whatsapp || '')}</p></div>
                      {addr.logradouro && (
                        <div className="bg-slate-800/50 p-3 rounded-lg md:col-span-2">
                          <span className="text-xs text-slate-500">Endereço</span>
                          <p className="text-white text-sm">{addr.logradouro}, {addr.numero || 'S/N'}{addr.complemento ? ` - ${addr.complemento}` : ''}</p>
                          <p className="text-slate-400 text-xs">{addr.bairro} - {addr.cidade}/{addr.estado} - CEP: {addr.cep}</p>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
              {/* Itens */}
              <div className="border-t border-slate-700 pt-3">
                <h4 className="text-white font-semibold mb-3">Itens e Custos</h4>
                {selectedOrder.items?.map((item, i) => {
                  const cost = getCostForItem(item.name, costConfig);
                  return (
                    <div key={i} className="bg-slate-800/50 p-3 rounded-lg mb-2">
                      <div className="flex justify-between"><span className="text-white font-medium">{item.name}</span><span className="text-cyan-400 font-bold">{formatCurrency(item.price)}</span></div>
                      <div className="flex justify-between mt-1 text-xs"><span className="text-slate-500">Custo: <span className="text-orange-400">{formatCurrency(cost)}</span></span><span className="text-green-400">Lucro: {formatCurrency(item.price - cost)}</span></div>
                    </div>
                  );
                })}
                <div className="mt-3 bg-gradient-to-r from-cyan-900/30 to-blue-900/30 border border-cyan-500/20 rounded-lg p-3">
                  {(() => {
                    const sale = selectedOrder.amount_paid || selectedOrder.total || 0;
                    const cost = (selectedOrder.items || []).reduce((s, i) => s + getCostForItem(i.name, costConfig) * (i.quantity || 1), 0);
                    return (<>
                      <div className="flex justify-between text-sm"><span className="text-slate-300">Venda:</span><span className="text-white font-bold">{formatCurrency(sale)}</span></div>
                      <div className="flex justify-between text-sm mt-1"><span className="text-slate-300">Custo:</span><span className="text-orange-400">{formatCurrency(cost)}</span></div>
                      <div className="flex justify-between text-sm mt-1 pt-1 border-t border-slate-700"><span className="text-white font-bold">Lucro:</span><span className="text-green-400 font-bold">{formatCurrency(sale - cost)}</span></div>
                    </>);
                  })()}
                </div>
              </div>
            </div>
            <Button onClick={() => setSelectedOrder(null)} className="w-full mt-4 bg-slate-700 hover:bg-slate-600">Fechar</Button>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminPedidos;
